/**
 * AssessmentFollowUpsBlock — renders the model's follow-up questions
 * (Phase X.3.5). The questions are gaps and ambiguities the operator
 * should resolve before finalizing, organized by section.
 *
 * Warm-cream/gold palette, mirroring EmailAnnotationsBlock (the same
 * "colleague sticky-note" register). Renders null when empty.
 */

import { type ReactNode } from 'react';
import type { AssessmentFollowUpQuestion } from '@/lib/api';

export interface AssessmentFollowUpsBlockProps {
  questions: readonly AssessmentFollowUpQuestion[];
}

export function AssessmentFollowUpsBlock({
  questions,
}: AssessmentFollowUpsBlockProps): ReactNode {
  if (questions.length === 0) return null;

  const containerStyle = {
    backgroundColor: '#FBF4E8',
    borderLeftColor: 'var(--color-warm-400)',
  } as const;
  const headingStyle = { color: '#7a5a1f' } as const;
  const bodyStyle = { color: 'var(--color-ink)' } as const;

  return (
    <section
      className="rounded-lg border-l-4 border-y border-r border-sage-800/[8%] p-4 space-y-2"
      style={containerStyle}
      data-testid="assessment-followups-block"
      aria-label="Follow-up questions"
    >
      <h3 className="text-sm font-semibold" style={headingStyle}>
        Before you finalize
      </h3>
      <ul className="space-y-2 list-none pl-0">
        {questions.map((q, i) => (
          <li key={i} className="text-sm leading-relaxed" style={bodyStyle}>
            <span className="font-semibold mr-1" style={headingStyle}>
              {q.section}:
            </span>
            <span>{q.question}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
